import { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { channelOrder, type ShareChannel, type ShareTarget } from '@tg/shared';
import { Button, CloseButton, Dialog } from '@/ui';
import { useLocale } from '@/i18n/useLocale';
import { toast } from '@/stores/toasts';
import { canSystemShare, runChannel, squareFile, type PictureChannel } from './useShareActions';
import { WeChatDialog } from './WeChatDialog';
import s from './share.module.css';

/** buttons before "More": the longest primary list of a locale (en, share.ts PRIMARY) fits whole */
const PRIMARY_COUNT = 6;

export interface ShareSheetProps {
  open: boolean;
  onClose(): void;
  /** creates / fetches the share target (POST /api/share/awards/:id, /moments, GET /invite) */
  load: () => Promise<ShareTarget>;
}

/**
 * "Share" panel: preview card, channels in the order of the player's language, the channel goes into the link (?c=)
 * for attribution. Silent (no sound), no celebration, no token amounts — the card and the text come from the server.
 */
export function ShareSheet({ open, onClose, load }: ShareSheetProps) {
  const intl = useIntl();
  const locale = useLocale();
  const [target, setTarget] = useState<ShareTarget | null>(null);
  const [more, setMore] = useState(false);
  const [picture, setPicture] = useState<PictureChannel | null>(null);
  const [manualCopy, setManualCopy] = useState<string | null>(null);
  const file = useRef<Promise<File | null> | undefined>(undefined);
  // callers pass inline lambdas — keep the latest without reloading the target on every render
  const loadRef = useRef(load);
  const closeRef = useRef(onClose);
  loadRef.current = load;
  closeRef.current = onClose;

  const brand = intl.formatMessage({ id: 'common.brand' });
  const fileName = `${brand}.png`;

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setTarget(null);
    setMore(false);
    setPicture(null);
    setManualCopy(null);
    file.current = undefined;
    loadRef.current().then(
      (t) => {
        if (!alive) return;
        // fetch the square card now: the system share must run inside the tap's user activation
        if (canSystemShare()) file.current = squareFile(t.images.sq, fileName);
        setTarget(t);
      },
      (e: unknown) => {
        if (!alive) return;
        toast.error(e);
        closeRef.current();
      },
    );
    return () => {
      alive = false;
    };
    // fileName follows the UI language, which does not change while the sheet is open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 'qr' is the attribution channel of the card's QR code, not a button; 'sys' only where the browser has it
  const order = channelOrder(locale).filter((c) => c !== 'qr' && (c !== 'sys' || canSystemShare()));
  const shown = more ? order : order.slice(0, PRIMARY_COUNT);

  const click = async (ch: ShareChannel) => {
    if (!target) return;
    const r = await runChannel(ch, target, { fileName, file: file.current });
    if (!r) return;
    if ('picture' in r) setPicture(r.picture);
    else setManualCopy(r.copyFailed);
  };

  return (
    <>
      <Dialog open={open && !picture} onClose={onClose} label={intl.formatMessage({ id: 'share.title' })} width={560}>
        <div className={s.sheet}>
          <div className={s.head}>
            <h2 className={s.title}>
              <FormattedMessage id="share.title" />
            </h2>
            <CloseButton onClick={onClose} />
          </div>
          {target ? (
            <img className={s.preview} src={target.images.og} alt={target.text} width={1200} height={630} />
          ) : (
            <div className={s.previewSkeleton} aria-busy="true" />
          )}
          <p className={s.note}>
            <FormattedMessage id="share.card.free" />
          </p>
          <div className={s.channels}>
            {shown.map((ch) => (
              <Button
                key={ch}
                data-channel={ch}
                tone="neutral"
                variant="outline"
                className={s.channel}
                disabled={!target}
                onClick={() => void click(ch)}
              >
                <FormattedMessage id={`share.channel.${ch}`} />
              </Button>
            ))}
            {!more && order.length > PRIMARY_COUNT ? (
              <Button tone="neutral" variant="ghost" className={s.channel} onClick={() => setMore(true)}>
                <FormattedMessage id="share.more" />
              </Button>
            ) : null}
          </div>
          {manualCopy ? (
            <input
              className={s.copyField}
              readOnly
              value={manualCopy}
              aria-label={intl.formatMessage({ id: 'share.channel.cp' })}
              autoFocus
              onFocus={(e) => e.currentTarget.select()}
            />
          ) : null}
        </div>
      </Dialog>
      {target && picture ? (
        <WeChatDialog open={open} onClose={() => setPicture(null)} image={target.images.sq} channel={picture} fileName={fileName} />
      ) : null}
    </>
  );
}
